import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";

import { VbangoRoomGameComponent } from "./component";
import {
  decodeArrangement,
  encodeArrangement,
  generateArrangement,
  isRoomRouteCredential,
  OWNER_IDENTITY,
  RoomColor,
} from "./room-route-codec";

type EnrollmentPhase = "choosing" | "learning" | "practice-one" | "practice-two" | "ready" | "dispatched";
interface LearningPosition { color: RoomColor; cell: number; }
const COLOR_NAMES: Readonly<Record<RoomColor, string>> = { R: "红", Y: "黄", B: "蓝", G: "绿" };

@Component({
  selector: "vbango-room-enrollment",
  standalone: true,
  imports: [CommonModule, VbangoRoomGameComponent],
  templateUrl: "./room-enrollment.component.html",
  styles: [`
:host { display: block; position: fixed; inset: 0; z-index: 600; overflow: auto; color-scheme: dark; color: #e7e0cf; background: radial-gradient(ellipse at 70% 37%, #28393266, transparent 53%), #101a1e; font-family: "Microsoft YaHei", "PingFang SC", sans-serif; }
*, *::before, *::after { box-sizing: border-box; }
.enrollment { max-width: 1120px; min-height: 100%; margin: auto; padding: 38px 34px; }
.enrollment-header { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 30px; }
.enrollment-brand { margin: 0; color: #d4c49d; font-family: "STSong", "SimSun", serif; font-size: 20px; font-weight: 400; letter-spacing: 7px; }
.enrollment-clear { padding: 8px 13px; border: 1px solid #cdb7842e; border-radius: 6px; color: #bdb191; background: transparent; font-size: 11px; cursor: pointer; }
.enrollment-grid { display: grid; grid-template-columns: minmax(0, .8fr) minmax(0, 1.2fr); gap: 42px; align-items: start; }
.enrollment-notes { padding: 17px 0; min-width: 0; }
.enrollment h2 { font-size: 24px; font-weight: 400; letter-spacing: 2px; margin: 0 0 20px; color: #e9dec1; }
.enrollment p { color: #aaa99a; font-size: 12px; line-height: 1.9; margin: 0 0 18px; }
.enrollment .private-copy { font-size: 11px; color: #bba981; }
.enrollment button { font: inherit; -webkit-tap-highlight-color: transparent; }
.enrollment button:focus-visible, .enrollment input:focus-visible { outline: 2px solid #e5d8b4; outline-offset: 4px; }
.enrollment button:disabled { cursor: default; opacity: .42; }
.action { display: inline-flex; justify-content: center; align-items: center; padding: 12px 19px; border: 1px solid #c7b17c71; border-radius: 6px; background: linear-gradient(135deg, #9f8a4f25, #93894e0b); color: #eadbbb; font-size: 12px !important; cursor: pointer; transition: background .2s, border-color .2s; }
.action:hover:not(:disabled) { background: #bca05d29; border-color: #dcc187a3; }
.quiet { border: 0; background: none; color: #b4a583; font-size: 11px !important; padding: 10px 0; cursor: pointer; text-decoration: underline; text-underline-offset: 4px; }
.enrollment-actions { display: flex; flex-wrap: wrap; gap: 13px; align-items: center; margin: 22px 0 12px; }
.learning-heading { display: flex; justify-content: space-between; align-items: center; margin-top: 23px; padding-top: 17px; border-top: 1px solid #c8b9811d; }
.learning-heading h3 { margin: 0; font-size: 12px; font-weight: 400; color: #d8c59c; }
.move-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 3px 14px; max-height: 332px; overflow: auto; margin: 12px 0 20px; padding: 10px 14px 10px 30px; border: 1px solid #cdb78421; border-radius: 8px; background: #070e122b; font-size: 11px; line-height: 2.2; color: #b8bdaf; }
.move-list li { padding-left: 2px; white-space: nowrap; }
.move-list .move-color { color: #e0cc9e; padding-right: 5px; }
.practice-indicator { display: flex; gap: 10px; margin: 25px 0 18px; }
.practice-indicator span { flex: 1; height: 3px; background: #cbb77916; border-radius: 2px; }.practice-indicator span.done { background: #b9aa72b0; }
.enrollment .status { min-height: 23px; margin-top: 13px; color: #c4b58f; font-size: 11px; }
.recovery { margin-top: 20px; padding-top: 20px; border-top: 1px solid #c8b98125; }
.acknowledgement { display: flex; gap: 9px; align-items: start; color: #c5c3b2; font-size: 11px; line-height: 1.9; margin: 21px 0; }
.acknowledgement input { margin: 4px 0 0; accent-color: #b4a576; }
.enrollment-preview { min-width: 0; border: 1px solid #bfb08624; border-radius: 18px; overflow: hidden; box-shadow: 0 18px 40px #0005; }
.empty-note { display: block; margin-top: 25px; color: #8e978f; font-size: 10px; line-height: 1.8; }
@media(max-width: 780px) { .enrollment { padding: 24px 22px; }.enrollment-grid { grid-template-columns: 1fr; gap: 23px; }.enrollment-header { margin-bottom: 13px; }.enrollment-notes { padding: 12px 0 0; }.enrollment h2 { font-size: 22px; }.move-list { max-height: 255px; }.enrollment-preview { order: 2; } }
@media(prefers-reduced-motion: reduce) { * { transition-duration: .01ms !important; } }
  `],
})
export class VbangoRoomEnrollmentComponent implements OnChanges, OnDestroy {
  @Input() busy = false;
  @Output() readonly secretSubmitted = new EventEmitter<string>();
  @ViewChild(VbangoRoomGameComponent) private practiceRoom?: VbangoRoomGameComponent;

  phase: EnrollmentPhase = "choosing";
  learningPositions: readonly LearningPosition[] = [];
  showLearning = false;
  acknowledged = false;
  recoveryDownloaded = false;
  statusText = "";
  submissionFailed = false;
  private canonical = "";
  private downloadUrl: string | null = null;
  private downloadTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  get hasRoute(): boolean { return this.canonical !== ""; }
  get practicing(): boolean { return this.phase === "practice-one" || this.phase === "practice-two"; }
  get firstPracticeDone(): boolean { return this.phase === "practice-two" || this.phase === "ready" || this.phase === "dispatched"; }
  get secondPracticeDone(): boolean { return this.phase === "ready" || this.phase === "dispatched"; }
  get canContinue(): boolean {
    return !this.busy && !this.destroyed && this.phase === "ready" && this.acknowledged && this.recoveryDownloaded && isRoomRouteCredential(this.canonical);
  }

  colorName(color: RoomColor): string { return COLOR_NAMES[color]; }
  cornerName(cell: number): string { return ({ 0: "左上角", 3: "右上角", 12: "左下角", 15: "右下角" } as Record<number, string>)[cell] ?? ""; }

  generate(): void {
    if (this.busy || this.destroyed || this.phase === "dispatched") return;
    this.clearPrivateState();
    try {
      const canonical = encodeArrangement(generateArrangement());
      this.choosePattern(canonical);
    } catch {
      this.clearPrivateState();
      this.statusText = "暂时无法选择排列，请稍后再试。";
    }
  }

  beginPractice(): void {
    if (this.busy || this.destroyed || this.phase !== "learning" || !isRoomRouteCredential(this.canonical)) return;
    this.showLearning = false;
    this.phase = "practice-one";
    this.practiceRoom?.rejectAttempt();
    this.statusText = "请重现您选择的四色位置，移动顺序不限。";
  }

  toggleLearning(): void {
    if (!this.busy && this.hasRoute && this.phase !== "dispatched") this.showLearning = !this.showLearning;
  }

  practiceSubmitted(value: string): void {
    if (this.destroyed || this.busy) return;
    if (this.phase === "choosing") {
      this.choosePattern(value);
      return;
    }
    if (!this.practicing) return;
    this.practiceRoom?.rejectAttempt();
    if (!isRoomRouteCredential(value) || value !== this.canonical) {
      this.statusText = "这次颜色位置与您设置的排列不同，请再试一次。";
      return;
    }
    if (this.phase === "practice-one") {
      this.phase = "practice-two";
      this.statusText = "第一遍一致。请再摆一次，移动顺序不限。";
    } else {
      this.phase = "ready";
      this.showLearning = false;
      this.statusText = "两遍颜色位置一致。";
    }
  }

  acknowledge(event: Event): void {
    if (!this.busy && event.target instanceof HTMLInputElement) this.acknowledged = event.target.checked;
  }

  downloadRecovery(): void {
    if (this.busy || this.destroyed || !this.hasRoute || this.phase === "dispatched" || !isRoomRouteCredential(this.canonical)) return;
    this.releaseDownload();
    const recovery = {
      format: "vbango-room-recovery-vgc2",
      website: "https://vault.vbango.com",
      identity: OWNER_IDENTITY,
      masterPassword: this.canonical,
      arrangement: decodeArrangement(this.canonical),
    };
    const blob = new Blob([JSON.stringify(recovery, null, 2)], { type: "application/json" });
    this.downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = this.downloadUrl;
    link.download = "guiguang-recovery-vgc2.json";
    link.rel = "noopener";
    link.click();
    link.remove();
    this.recoveryDownloaded = true;
    this.downloadTimer = setTimeout(() => this.releaseDownload(), 30_000);
    this.statusText = "恢复文件已交给浏览器下载。请确认文件已经保存。";
  }

  continue(): void {
    if (!this.canContinue) return;
    const credential = this.canonical;
    this.phase = "dispatched";
    this.canonical = "";
    this.learningPositions = [];
    this.showLearning = false;
    this.acknowledged = false;
    this.recoveryDownloaded = false;
    this.releaseDownload();
    this.practiceRoom?.rejectAttempt();
    this.secretSubmitted.emit(credential);
  }

  clear(): void {
    if (!this.busy && !this.destroyed && this.phase !== "dispatched") this.clearPrivateState();
  }

  rejectAttempt(): void {
    if (this.destroyed) return;
    this.canonical = "";
    this.learningPositions = [];
    this.showLearning = false;
    this.acknowledged = false;
    this.recoveryDownloaded = false;
    this.releaseDownload();
    this.practiceRoom?.rejectAttempt();
    this.phase = "dispatched";
    this.submissionFailed = true;
    this.statusText = "设置结果尚待确认。请保留已下载的恢复文件，确认账户状态后再继续。";
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["busy"] && this.busy) this.releaseDownload();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearPrivateState();
    this.secretSubmitted.complete();
  }

  private clearPrivateState(): void {
    this.canonical = "";
    this.learningPositions = [];
    this.showLearning = false;
    this.acknowledged = false;
    this.recoveryDownloaded = false;
    this.phase = "choosing";
    this.statusText = "";
    this.submissionFailed = false;
    this.releaseDownload();
    this.practiceRoom?.rejectAttempt();
  }

  private choosePattern(value: string): void {
    const positions = decodeArrangement(value);
    if (!positions || !isRoomRouteCredential(value)) {
      this.practiceRoom?.rejectAttempt();
      this.statusText = "请选择自己的四色排列。";
      return;
    }
    this.canonical = value;
    this.learningPositions = (["R", "Y", "B", "G"] as RoomColor[]).map((color) => ({ color, cell: positions[color] }));
    this.showLearning = true;
    this.phase = "learning";
    this.acknowledged = false;
    this.recoveryDownloaded = false;
    this.releaseDownload();
    this.practiceRoom?.rejectAttempt();
    this.statusText = "已记下您选择的四色位置。";
  }

  private releaseDownload(): void {
    if (this.downloadTimer !== null) clearTimeout(this.downloadTimer);
    this.downloadTimer = null;
    if (this.downloadUrl !== null) URL.revokeObjectURL(this.downloadUrl);
    this.downloadUrl = null;
  }
}
